import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import { Spinner } from './spinner';

export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-medium transition-[background,color,box-shadow,transform] duration-150 select-none active:translate-y-px disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** Ação principal da tela. No máximo uma por contexto. */
        primary: 'bg-signal text-signal-fg hover:bg-[#c8f52c] shadow-[0_0_0_1px_rgb(212_255_58/0.4)]',
        secondary: 'bg-raised text-fg shadow-[inset_0_0_0_1px_var(--color-line)] hover:bg-overlay',
        ghost: 'text-fg-muted hover:bg-hover hover:text-fg',
        danger: 'bg-danger/12 text-danger shadow-[inset_0_0_0_1px_rgb(242_113_95/0.25)] hover:bg-danger/20',
      },
      size: {
        sm: 'h-8 rounded-[9px] px-3 text-[13px]',
        md: 'h-9 rounded-control px-3.5 text-[13.5px]',
        lg: 'h-11 rounded-[12px] px-5 text-[14.5px]',
        icon: 'size-9 rounded-control',
        'icon-sm': 'size-8 rounded-[9px]',
      },
      pill: { true: 'rounded-full' },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean; loading?: boolean };

export function Button({
  className,
  variant,
  size,
  pill,
  asChild,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, pill }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Spinner />}
          {children}
        </>
      )}
    </Comp>
  );
}
